import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { getCroppedImg } from '../../utils/cropImage';

// ── aspect ratio presets ──────────────────────────────────────────────
const ASPECTS = [
  { key: 'original',  label: 'Original', ratio: undefined },
  { key: 'square',    label: '1:1',      ratio: 1 },
  { key: 'portrait',  label: '4:5',      ratio: 4 / 5 },
  { key: 'landscape', label: '16:9',     ratio: 16 / 9 },
];

const AspectIcon = ({ type }) => {
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 };
  if (type === 'original')  return <svg width="18" height="18" viewBox="0 0 20 20" {...s}><rect x="2" y="2" width="16" height="16" rx="1"/><line x1="2" y1="7" x2="18" y2="7"/><line x1="2" y1="13" x2="18" y2="13"/><line x1="7" y1="2" x2="7" y2="18"/><line x1="13" y1="2" x2="13" y2="18"/></svg>;
  if (type === 'square')    return <svg width="18" height="18" viewBox="0 0 20 20" {...s}><rect x="2" y="2" width="16" height="16" rx="1"/></svg>;
  if (type === 'portrait')  return <svg width="18" height="18" viewBox="0 0 20 20" {...s}><rect x="5" y="1" width="10" height="18" rx="1"/></svg>;
  if (type === 'landscape') return <svg width="18" height="18" viewBox="0 0 20 20" {...s}><rect x="1" y="5" width="18" height="10" rx="1"/></svg>;
  return null;
};

// ── per-photo factory ─────────────────────────────────────────────────
const MAX_FILE_BYTES = 20 * 1024 * 1024; // keep in sync with server middleware/upload.js

const makePhoto = (file) => ({
  file,
  url: URL.createObjectURL(file),
  crop: { x: 0, y: 0 },
  zoom: 1,
  croppedAreaPixels: null,
});

// ── main component ────────────────────────────────────────────────────
const PhotoEditorModal = ({ vehicles = [], onClose, onCreated }) => {
  const [photos, setPhotos]           = useState([]);
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [aspectKey, setAspectKey]     = useState('square');
  const [step, setStep]               = useState('select'); // 'select' | 'edit' | 'video' | 'details'
  const [caption, setCaption]         = useState('');
  const [selectedCars, setSelectedCars] = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [reorderMode, setReorderMode] = useState(false);

  // ── video post state ────────────────────────────────────────────────
  const [video, setVideo] = useState(null); // { file, url, w, h, duration }
  const [poster, setPoster] = useState(null); // { blob, url }
  const [scrubTime, setScrubTime] = useState(0);

  const fileInputRef = useRef();
  const moreInputRef = useRef();
  const videoInputRef = useRef();
  const videoPreviewRef = useRef();

  const aspect  = ASPECTS.find(a => a.key === aspectKey)?.ratio;
  const current = photos[currentIdx];

  // revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      photos.forEach(p => URL.revokeObjectURL(p.url));
      if (video?.url) URL.revokeObjectURL(video.url);
      if (poster?.url) URL.revokeObjectURL(poster.url);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── per-photo updater ───────────────────────────────────────────────
  const updatePhoto = (idx, updates) =>
    setPhotos(prev => prev.map((p, i) => i === idx ? { ...p, ...updates } : p));

  const onCropChange = useCallback(
    (crop) => setPhotos(prev => prev.map((p, i) => i === currentIdx ? { ...p, crop } : p)),
    [currentIdx]
  );
  const onZoomChange = useCallback(
    (zoom) => setPhotos(prev => prev.map((p, i) => i === currentIdx ? { ...p, zoom } : p)),
    [currentIdx]
  );
  const onCropComplete = useCallback(
    (_, pixels) => setPhotos(prev => prev.map((p, i) => i === currentIdx ? { ...p, croppedAreaPixels: pixels } : p)),
    [currentIdx]
  );

  // ── file handling ───────────────────────────────────────────────────
  const addFiles = (fileList) => {
    const remaining = 20 - photos.length;
    const incoming = Array.from(fileList).slice(0, remaining);
    const tooBig = incoming.filter(f => f.size > MAX_FILE_BYTES);
    const ok     = incoming.filter(f => f.size <= MAX_FILE_BYTES);

    if (tooBig.length) {
      const limitMb = Math.round(MAX_FILE_BYTES / (1024 * 1024));
      const names = tooBig.map(f => f.name).join(', ');
      toast.error(
        tooBig.length === 1
          ? `${names} is too large — max ${limitMb} MB per photo`
          : `${tooBig.length} photos are too large — max ${limitMb} MB per photo`
      );
    }
    if (!ok.length) return;

    const toAdd = ok.map(makePhoto);
    setPhotos(prev => [...prev, ...toAdd]);
    setCurrentIdx(prev => photos.length === 0 ? 0 : prev);
    if (step === 'select') setStep('edit');
  };

  const removePhoto = (idx, e) => {
    e.stopPropagation();
    URL.revokeObjectURL(photos[idx].url);
    setPhotos(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) setStep('select');
      return next;
    });
    setCurrentIdx(prev => Math.min(prev, Math.max(0, photos.length - 2)));
  };

  // ── reorder via arrow buttons ──────────────────────────────────────
  const swap = (a, b) => {
    if (a < 0 || b < 0 || a >= photos.length || b >= photos.length) return;
    setPhotos(prev => {
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
    setCurrentIdx(prev => {
      if (prev === a) return b;
      if (prev === b) return a;
      return prev;
    });
  };
  const moveLeft  = (e, idx) => { e.stopPropagation(); swap(idx, idx - 1); };
  const moveRight = (e, idx) => { e.stopPropagation(); swap(idx, idx + 1); };

  // ── aspect change: reset all crops ─────────────────────────────────
  const changeAspect = (key) => {
    setAspectKey(key);
    setPhotos(prev => prev.map(p => ({ ...p, crop: { x: 0, y: 0 }, zoom: 1, croppedAreaPixels: null })));
  };

  // ── video handling ──────────────────────────────────────────────────
  const MAX_VIDEO_SECONDS = 60;

  const onVideoSelected = (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      const limitMb = Math.round(MAX_FILE_BYTES / (1024 * 1024));
      toast.error(`${file.name} is too large — max ${limitMb} MB`);
      return;
    }
    const url = URL.createObjectURL(file);
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      const duration = probe.duration;
      const w = probe.videoWidth;
      const h = probe.videoHeight;
      if (duration > MAX_VIDEO_SECONDS) {
        URL.revokeObjectURL(url);
        toast.error(`Video is too long — max ${MAX_VIDEO_SECONDS} seconds`);
        return;
      }
      // clear any selected photos so we're in a clean single-video flow
      photos.forEach(p => URL.revokeObjectURL(p.url));
      setPhotos([]);
      setVideo({ file, url, w, h, duration });
      setPoster(null);
      setScrubTime(0);
      setStep('video');
    };
    probe.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error('Could not read that video file');
    };
    probe.src = url;
  };

  const onScrub = (t) => {
    setScrubTime(t);
    if (videoPreviewRef.current) videoPreviewRef.current.currentTime = t;
  };

  // capture the current preview frame to a jpeg blob; returns { blob, url }.
  // Seeking is async, so if a seek is in flight we wait for the 'seeked' event
  // (with a timeout fallback) before drawing — otherwise a scrub-then-capture
  // can grab the previous/blank frame.
  const captureFrame = () => new Promise((resolve, reject) => {
    const v = videoPreviewRef.current;
    if (!v || !video) return reject(new Error('no video'));
    const draw = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.w;
      canvas.height = video.h;
      const ctx = canvas.getContext('2d');
      try {
        ctx.drawImage(v, 0, 0, video.w, video.h);
      } catch (err) {
        return reject(err);
      }
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('toBlob failed'));
        resolve({ blob, url: URL.createObjectURL(blob) });
      }, 'image/jpeg', 0.85);
    };
    if (v.readyState >= 2 && !v.seeking) {
      draw();
    } else {
      let done = false;
      let timer;
      const finish = () => {
        if (done) return;
        done = true;
        v.removeEventListener('seeked', finish);
        clearTimeout(timer);
        draw();
      };
      timer = setTimeout(finish, 600);
      v.addEventListener('seeked', finish, { once: true });
    }
  });

  const useThisFrame = async () => {
    try {
      const next = await captureFrame();
      if (poster?.url) URL.revokeObjectURL(poster.url);
      setPoster(next);
    } catch {
      toast.error('Could not capture that frame');
    }
  };

  const clearVideo = () => {
    if (video?.url) URL.revokeObjectURL(video.url);
    if (poster?.url) URL.revokeObjectURL(poster.url);
    setVideo(null);
    setPoster(null);
    setScrubTime(0);
    setStep('select');
  };

  // ── submit ──────────────────────────────────────────────────────────
  const handleSubmitVideo = async () => {
    setSubmitting(true);
    try {
      // if the user never picked a frame, capture the current one (defaults to t=0)
      let chosen = poster;
      if (!chosen) {
        if (videoPreviewRef.current) {
          try { videoPreviewRef.current.currentTime = 0; } catch { /* ignore */ }
        }
        chosen = await captureFrame();
        setPoster(chosen);
      }

      const fd = new FormData();
      fd.append('video', video.file);
      fd.append('poster', new File([chosen.blob], 'poster.jpg', { type: 'image/jpeg' }));
      fd.append('content', caption);
      fd.append('postType', 'general');
      if (selectedCars.length > 0) fd.append('relatedVehicleId', selectedCars[0]);
      fd.append('imageMeta', JSON.stringify([{ w: video.w, h: video.h }]));

      await api.post('/feed/posts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Post created!');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create post';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (video) { await handleSubmitVideo(); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('content', caption);
      fd.append('postType', 'general');
      if (selectedCars.length > 0) fd.append('relatedVehicleId', selectedCars[0]);

      for (const photo of photos) {
        if (aspectKey !== 'original' && photo.croppedAreaPixels) {
          const cropped = await getCroppedImg(photo.url, photo.croppedAreaPixels);
          fd.append('images', cropped);
        } else {
          fd.append('images', photo.file);
        }
      }

      await api.post('/feed/posts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Post created!');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create post';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 'details') setStep(video ? 'video' : 'edit');
    else if (step === 'video') clearVideo();
    else onClose();
  };

  // ── render ──────────────────────────────────────────────────────────
  return (
    <div className="pe-overlay">
      <div className="pe-modal">

        {/* Header */}
        <div className="pe-header">
          <button className="pe-header-btn" onClick={handleBack}>
            {step === 'details' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            )}
          </button>
          <span className="pe-header-title">New Post</span>
          {((step === 'edit' && photos.length > 0) || step === 'video') && (
            <button className="pe-header-btn pe-header-btn--next" onClick={() => setStep('details')}>
              Next
            </button>
          )}
          {step === 'details' && (
            <button className="pe-header-btn pe-header-btn--next" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Sharing…' : 'Share'}
            </button>
          )}
          {(step === 'select' || (step === 'edit' && photos.length === 0)) && (
            <span style={{ width: 40 }} />
          )}
        </div>

        {/* ── STEP: SELECT ─────────────────────────────────────────── */}
        {step === 'select' && (
          <div className="pe-select">
            <div className="pe-select-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <p className="pe-select-hint">Select up to 20 photos, or one video (max 60s)</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button className="btn-primary" onClick={() => fileInputRef.current?.click()}>
                Choose Photos
              </button>
              <button className="btn-primary" onClick={() => videoInputRef.current?.click()}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                  Add Video
                </span>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file" accept="image/*" multiple
              style={{ display: 'none' }}
              onChange={e => addFiles(e.target.files)}
            />
            <input
              ref={videoInputRef}
              type="file" accept="video/*"
              style={{ display: 'none' }}
              onChange={e => onVideoSelected(e.target.files)}
            />
          </div>
        )}

        {/* ── STEP: EDIT ───────────────────────────────────────────── */}
        {step === 'edit' && current && (
          <div className="pe-edit">

            {/* Crop area */}
            <div className="pe-crop-wrap">
              <Cropper
                image={current.url}
                crop={current.crop}
                zoom={current.zoom}
                aspect={aspect}
                minZoom={1}
                maxZoom={5}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropComplete}
                showGrid={false}
                style={{ containerStyle: { background: '#06080f' } }}
              />
              {/* Left/right nav arrows */}
              {photos.length > 1 && currentIdx > 0 && (
                <button className="pe-nav pe-nav--left" onClick={() => setCurrentIdx(i => i - 1)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
              )}
              {photos.length > 1 && currentIdx < photos.length - 1 && (
                <button className="pe-nav pe-nav--right" onClick={() => setCurrentIdx(i => i + 1)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              )}
              {/* Dot indicators */}
              {photos.length > 1 && (
                <div className="pe-dots">
                  {photos.map((_, i) => (
                    <button key={i} className={`pe-dot${i === currentIdx ? ' pe-dot--active' : ''}`} onClick={() => setCurrentIdx(i)} />
                  ))}
                </div>
              )}
            </div>

            {/* Zoom slider */}
            <div className="pe-zoom">
              <span className="pe-zoom-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input
                type="range" min={1} max={5} step={0.01}
                value={current.zoom}
                onChange={e => onZoomChange(Number(e.target.value))}
                className="pe-zoom-slider"
              />
              <span className="pe-zoom-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
            </div>

            {/* Aspect ratio buttons */}
            <div className="pe-aspects">
              {ASPECTS.map(a => (
                <button
                  key={a.key}
                  className={`pe-aspect-btn${aspectKey === a.key ? ' active' : ''}`}
                  onClick={() => changeAspect(a.key)}
                >
                  <AspectIcon type={a.key} />
                  <span>{a.label}</span>
                </button>
              ))}
            </div>

            {/* Thumbnail strip */}
            <div className="pe-strip">
              {photos.length > 1 && (
                <button
                  type="button"
                  className={`pe-reorder-toggle${reorderMode ? ' pe-reorder-toggle--active' : ''}`}
                  onClick={() => setReorderMode(m => !m)}
                  title={reorderMode ? 'Done reordering' : 'Reorder photos'}
                >
                  {reorderMode ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="8" x2="20" y2="8"/><polyline points="8 4 4 8 8 12"/><line x1="4" y1="16" x2="20" y2="16"/><polyline points="16 12 20 16 16 20"/></svg>
                  )}
                </button>
              )}
              {photos.map((p, i) => (
                <div key={p.url} className="pe-thumb-wrap">
                  <div
                    className={`pe-thumb${i === currentIdx ? ' pe-thumb--active' : ''}`}
                    onClick={() => setCurrentIdx(i)}
                  >
                    <img src={p.url} alt="" draggable={false} />
                    <span className="pe-thumb-num">{i + 1}</span>
                    {!reorderMode && (
                      <button className="pe-thumb-remove" onClick={e => removePhoto(i, e)}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                  {reorderMode && (
                    <div className="pe-thumb-arrows">
                      <button
                        className="pe-arrow-btn"
                        onClick={(e) => moveLeft(e, i)}
                        disabled={i === 0}
                        title="Move left"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                      </button>
                      <button
                        className="pe-arrow-btn"
                        onClick={(e) => moveRight(e, i)}
                        disabled={i === photos.length - 1}
                        title="Move right"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {photos.length < 20 && (
                <>
                  <button className="pe-thumb-add" onClick={() => moreInputRef.current?.click()}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                  <input
                    ref={moreInputRef}
                    type="file" accept="image/*" multiple
                    style={{ display: 'none' }}
                    onChange={e => addFiles(e.target.files)}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* ── STEP: VIDEO ──────────────────────────────────────────── */}
        {step === 'video' && video && (
          <div className="pe-edit">

            {/* Video preview */}
            <div className="pe-crop-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#06080f' }}>
              <video
                ref={videoPreviewRef}
                src={video.url}
                preload="metadata"
                playsInline
                muted
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                onLoadedMetadata={() => { if (videoPreviewRef.current) videoPreviewRef.current.currentTime = scrubTime; }}
              />
            </div>

            {/* Poster scrubber */}
            <div className="pe-zoom">
              <span className="pe-zoom-icon" title="Choose poster frame">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </span>
              <input
                type="range" min={0} max={video.duration || 0} step={0.05}
                value={scrubTime}
                onChange={e => onScrub(Number(e.target.value))}
                className="pe-zoom-slider"
              />
              <span className="pe-zoom-icon" style={{ minWidth: 40, fontSize: 12 }}>
                {scrubTime.toFixed(1)}s
              </span>
            </div>

            {/* Frame actions */}
            <div className="pe-aspects" style={{ alignItems: 'center' }}>
              <button className="pe-aspect-btn" onClick={useThisFrame}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                <span>Use this frame</span>
              </button>
              {poster && (
                <div className="pe-thumb pe-thumb--active" style={{ cursor: 'default' }} title="Chosen poster">
                  <img src={poster.url} alt="" draggable={false} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP: DETAILS ────────────────────────────────────────── */}
        {step === 'details' && (
          <div className="pe-details">
            {/* Left: media preview */}
            {video ? (
              <div className="pe-preview-strip">
                <div className="pe-preview-thumb pe-preview-thumb--hero" onClick={() => setStep('video')} title="Click to re-pick poster">
                  <img src={poster?.url || video.url} alt="" />
                </div>
              </div>
            ) : (
            <div className="pe-preview-strip">
              {photos.map((p, i) => (
                <div
                  key={p.url}
                  className={`pe-preview-thumb${i === 0 ? ' pe-preview-thumb--hero' : ''}`}
                  onClick={() => { setCurrentIdx(i); setStep('edit'); }}
                  title="Click to re-edit"
                >
                  <img src={p.url} alt="" />
                  {photos.length > 1 && (
                    <span className="pe-preview-num">{i + 1}</span>
                  )}
                </div>
              ))}
            </div>
            )}

            {/* Right: caption + tag cars */}
            <div className="pe-details-form">
              <textarea
                className="pe-caption"
                placeholder="Write a caption…"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                rows={5}
              />
              {vehicles.length > 0 && (
                <div className="pe-tag-section">
                  <p className="pe-tag-label">Tag a car</p>
                  <div className="pe-tag-list">
                    {vehicles.map(uv => (
                      <label
                        key={uv.id}
                        className={`pe-tag-item${selectedCars.includes(uv.id) ? ' active' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCars.includes(uv.id)}
                          onChange={() => setSelectedCars(prev =>
                            prev.includes(uv.id)
                              ? prev.filter(c => c !== uv.id)
                              : [...prev, uv.id]
                          )}
                        />
                        {uv.vehicle?.primaryImageUrl && (
                          <img src={uv.vehicle.primaryImageUrl} alt="" className="pe-tag-car-img" />
                        )}
                        <span>{uv.nickname || `${uv.vehicle?.year} ${uv.vehicle?.make} ${uv.vehicle?.model}`}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PhotoEditorModal;
